import { useMemo } from 'react'

import { useApiGetEvaluationResult } from 'api/kinerja/kepala-sekolah/penilaian-evaluasi'
import { useApiGetPenilaianProgress } from 'api/kinerja/kepala-sekolah/penilaian'
import { NotFoundError } from 'utils/customError'

export default function useEvaluationResult({ npsn }: { npsn: string }) {
  const {
    // Map passed data to progress. If data is not ready, return null.
    data: { data: progress, meta } = { data: null },
    isLoading: isLoadingProgress,
    isSuccess: isSuccessfullyLoadProgress,
    isError: isFailedLoadProgress,
  } = useApiGetPenilaianProgress({ npsn }, { enabled: !!npsn })

  // Define the state of the evaluation result. This page should only be shown if the evaluation result is DINAS_TO_KS.
  const isSent =
    isSuccessfullyLoadProgress &&
    ['DINAS_TO_KS'].includes(progress?.statePengiriman)

  const isFinal =
    isSuccessfullyLoadProgress && progress?.statePengiriman === 'DINAS_TO_KS'

  if (isSuccessfullyLoadProgress && !isSent) {
    throw new NotFoundError(
      '/pengelolaan-kinerja/kepala-sekolah/pegawai/hasil-evaluasi',
      new Error()
    )
  }

  const {
    // Map passed data to result. If data is not ready, return null.
    data: { data: result } = { data: null },
    isLoading: isLoadingEvaluationResult,
    isSuccess: isSuccessfullyLoadEvaluationResult,
    isError: isFailedLoadEvaluationResult,
  } = useApiGetEvaluationResult(npsn, {
    enabled: isSuccessfullyLoadProgress && isSent,
    useErrorBoundary: true,
  })

  // Define the state of the page
  const isLoading = isLoadingProgress || isLoadingEvaluationResult
  const isSuccess =
    !isLoading &&
    isSuccessfullyLoadProgress &&
    isSuccessfullyLoadEvaluationResult
  const isError =
    !isLoading && (isFailedLoadProgress || isFailedLoadEvaluationResult)

  // Define the data to be shown in the page
  const mappedResult = useMemo(() => {
    if (!result) return null

    return {
      details: [
        {
          label: 'Pegawai yang Dinilai',
          items: [
            { label: 'Nama', value: result?.ks?.nama },
            { label: 'NIP', value: result?.ks?.nip },
            { label: 'Jabatan', value: result?.ks?.jabatan },
            { label: 'Unit Kerja', value: result?.ks?.unitKerja },
          ],
        },
        {
          label: 'Pejabat Penilai Kinerja',
          items: [
            { label: 'Nama', value: result?.dinas?.nama },
            { label: 'NIP', value: result?.dinas?.nip },
            { label: 'Jabatan', value: result?.dinas?.jabatan },
            { label: 'Unit Kerja', value: result?.dinas?.unitKerja },
          ],
        },
        ,
      ],
      predicates: [
        {
          label: 'Praktik Kinerja',
          description:
            'Berdasarkan dokumen Tindak Lanjut dan Refleksi Tindak Lanjut.',
          value: result?.predikatKinerja?.praktikKinerja,
        },
        {
          label: 'Perilaku Kerja',
          description: 'Berdasarkan penilaian terhadap 7 aspek Perilaku Kerja.',
          value: result?.predikatKinerja?.perilakuKerja,
        },
      ],
      summary: {
        label: 'Predikat Kinerja',
        value: result?.predikatKinerja?.predikatKinerja,
      },
    }
  }, [result])

  return {
    isLoading,
    isSuccess,
    isError,
    isFinal,
    result,
    mappedResult,
    meta,
  }
}
